import { PricingMonthlyRow, PricingRules } from './api';
import { DEFAULT_RULES, monthlyLines } from './pricingMath';
import { chargeRate, MONTHLY_RULES } from './monthlySimpleMath';
import { monthlyCaseScopeIssue } from './monthlyCaseScope';

const nonnegative = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 10000000;
const whole = (value: unknown): value is number => nonnegative(value) && Number.isInteger(value);

export function monthlyRowIssue(row: PricingMonthlyRow, rules: PricingRules = DEFAULT_RULES, availableCaseNames?: readonly string[]): string | null {
  if (!row.label.trim()) return 'Enter a service name.';
  const scopeIssue = monthlyCaseScopeIssue(row.caseNames, availableCaseNames);
  if (scopeIssue) return scopeIssue;
  if (row.model === 'simple') {
    if (!row.simple?.charges.length) return 'Add a price for this service.';
    for (const charge of row.simple.charges) {
      const chargeScopeIssue = monthlyCaseScopeIssue(charge.caseNames, availableCaseNames);
      if (chargeScopeIssue) return chargeScopeIssue;
      if (!MONTHLY_RULES[charge.ruleId]) return 'Review this service’s pricing terms.';
      if (!charge.label.trim()) return 'Enter a charge name.';
      if (charge.quantity != null && !whole(charge.quantity)) return `Enter a whole-number volume for ${charge.label}.`;
      if (charge.minimum != null && !whole(charge.minimum)) return `Enter a whole-number minimum for ${charge.label}.`;
      if (charge.ruleId === 'voice' && (!whole(charge.minimum) || charge.minimum === 0)) return `Set the agreed monthly minimum for ${charge.label}.`;
      if ((charge.ruleId === 'phase_addon' || charge.ruleId === 'phase_user') && !nonnegative(charge.manualRate)) return `Set the agreed rate for ${charge.label}.`;
      if (charge.unitCost != null && !nonnegative(charge.unitCost)) return `Check the cost for ${charge.label}.`;
      if (MONTHLY_RULES[charge.ruleId].bucket !== 'third_party' && chargeRate(charge) == null) return `Set the price for ${charge.label}.`;
    }
    return null;
  }
  if (row.model === 'fixed' || row.model === 'usage') {
    if (row.model === 'fixed' && !nonnegative(row.amount)) return 'Enter the monthly fee.';
    if (row.model === 'usage' && ((row.rate != null && !nonnegative(row.rate)) || (row.minimum != null && !whole(row.minimum)))) return 'Check the usage rate and minimum.';
    return monthlyLines(row, rules).price ? null : 'Set the price for this service.';
  }
  return row.price?.trim() ? null : 'Enter the price terms.';
}

export const monthlyRowsReady = (rows: PricingMonthlyRow[], rules: PricingRules = DEFAULT_RULES, availableCaseNames?: readonly string[]) =>
  rows.length > 0 && rows.every(row => monthlyRowIssue(row, rules, availableCaseNames) === null);
